import { buildPackFollowupSchedule, resolveSentPackReviewTemplate } from './packSystem';
import { computeFeedbackTrends } from './feedbackTrends';
import { matchesScheduledReview } from './renewalOfferPublisher';

const DAY_MS = 86400000;
const REVIEW_FIELDS = Object.freeze([
  'adherence',
  'cheats',
  'progress',
  'energy',
  'digestion',
  'difficulty',
  'organisation',
  'main_issue',
]);
const COMPARABLE_FIELDS = Object.freeze([
  'adherence',
  'cheats',
  'progress',
  'energy',
  'digestion',
  'difficulty',
  'organisation',
]);
const REQUIRED_REVIEW_FIELDS = Object.freeze([
  'adherence',
  'progress',
  'difficulty',
  'main_issue',
]);

const FIELD_LABELS = Object.freeze({
  adherence: 'Adhérence déclarée',
  cheats: 'Écarts déclarés',
  progress: 'Progression déclarée',
  energy: 'Énergie déclarée',
  digestion: 'Digestion déclarée',
  difficulty: 'Difficulté du plan',
  organisation: 'Organisation des repas',
  main_issue: 'Problème principal déclaré',
});

const VALUE_LABELS = Object.freeze({
  adherence: Object.freeze({ '100': 'Tous les jours', '75': '75 % du temps', '50': '50 % du temps', '<50': 'Moins de 50 %' }),
  cheats: Object.freeze({ none: 'Aucun', occasional: 'Occasionnels', frequent: 'Fréquents' }),
  progress: Object.freeze({ yes: 'Oui clairement', little: 'Un peu', none: 'Pas encore' }),
  energy: Object.freeze({ high: 'Meilleure', normal: 'Stable', low: 'Moins bonne' }),
  digestion: Object.freeze({ good: 'Bonne', average: 'Moyenne', bad: 'Difficile' }),
  difficulty: Object.freeze({ easy: 'Facile à suivre', ok: 'Correct', hard: 'Trop difficile' }),
  organisation: Object.freeze({ simple: 'Simple', medium: 'Gérable', complex: 'Compliquée' }),
  main_issue: Object.freeze({
    time: 'Manque de temps',
    taste: 'Aliments pas appréciés',
    hunger: 'Faim entre les repas',
    cost: 'Coût alimentaire',
    social: 'Social / restaurants',
    motivation: 'Motivation',
    complexity: 'Plan trop complexe',
    other: 'Autre',
  }),
});

function timestamp(value) {
  const parsed = Date.parse(value || '');
  return Number.isFinite(parsed) ? parsed : null;
}

function reviewTimestamp(review) {
  return timestamp(review?.submitted_at || review?.created_at);
}

function hasExplicitMatchingTemplate(review, scheduleEntry) {
  const reviewTemplate = typeof review?.template_type === 'string'
    ? review.template_type
    : '';
  if (!reviewTemplate) return false;
  const scheduledTemplate = resolveSentPackReviewTemplate(scheduleEntry);
  return scheduledTemplate === reviewTemplate;
}

export function isCycleReviewPayloadExploitable(review) {
  if (!review || review.status !== 'submitted') return false;
  for (const field of REVIEW_FIELDS) {
    const value = review[field];
    const required = REQUIRED_REVIEW_FIELDS.includes(field);
    if (value === null || value === undefined || value === '') {
      if (required) return false;
      continue;
    }
    if (typeof value !== 'string' || !Object.hasOwn(VALUE_LABELS[field], value)) {
      return false;
    }
  }
  if (
    review.main_issue_text !== null
    && review.main_issue_text !== undefined
    && (
      typeof review.main_issue_text !== 'string'
      || Array.from(review.main_issue_text).length > 80
    )
  ) {
    return false;
  }
  return true;
}

function boundedSingleLine(value, maxLength = 800) {
  if (typeof value !== 'string') return '';
  return Array.from(value.replace(/[\t\n\r\f\v ]+/g, ' ').trim()).slice(0, maxLength).join('');
}

function labelForValue(field, value) {
  if (value === null || value === undefined || String(value).trim() === '') return null;
  const normalized = String(value).trim();
  return VALUE_LABELS[field]?.[normalized] || normalized;
}

function reviewWeekLabel(review, schedule = []) {
  const stepNumber = Number(review?.step_number);
  const step = (Array.isArray(schedule) ? schedule : []).find((item) => item?.stepNumber === stepNumber);
  if (Number.isFinite(step?.weekOffset)) return `Semaine ${step.weekOffset}`;
  return Number.isInteger(stepNumber) && stepNumber > 0 ? `Bilan n°${stepNumber}` : 'Bilan de cycle';
}

function selectScheduledSubmittedCycleReviews(reviews = [], client = null) {
  const start = timestamp(client?.packStartedAt);
  const schedule = buildPackFollowupSchedule(client);
  return (Array.isArray(reviews) ? reviews : [])
    .filter((review) => review?.status === 'submitted')
    .filter((review) => {
      const submittedAt = reviewTimestamp(review);
      if (submittedAt === null) return false;
      const scheduleEntry = schedule.find((entry) => entry.stepNumber === review.step_number);
      return review.client_id === client?.id
        && matchesScheduledReview(review, scheduleEntry)
        && scheduleEntry?.type === 'review'
        && (scheduleEntry?.status === 'sent' || scheduleEntry?.status === 'done')
        && start !== null
        && submittedAt >= start;
    })
    // L'ordre du cycle vient du jalon serveur, jamais de l'heure de soumission :
    // un bilan S4 envoyé tardivement ne peut pas devenir le successeur de S8.
    .sort((left, right) => left.step_number - right.step_number);
}

export function selectCurrentPackCycleReviews(reviews = [], client = null) {
  const schedule = buildPackFollowupSchedule(client);
  return selectScheduledSubmittedCycleReviews(reviews, client).filter((review) => {
    const scheduleEntry = schedule.find((entry) => entry.stepNumber === review.step_number);
    return hasExplicitMatchingTemplate(review, scheduleEntry)
      && isCycleReviewPayloadExploitable(review);
  });
}

export function selectCurrentSentCycleReview(reviews = [], client = null) {
  const start = timestamp(client?.packStartedAt);
  if (start === null) return null;
  const schedule = buildPackFollowupSchedule(client);
  return (Array.isArray(reviews) ? reviews : [])
    .filter((review) => review?.status === 'sent' && review?.client_id === client?.id)
    .filter((review) => {
      const createdAt = timestamp(review?.created_at);
      const scheduleEntry = schedule.find((entry) => entry.stepNumber === review.step_number);
      return createdAt !== null
        && createdAt >= start
        && scheduleEntry?.reviewId === review.id
        && scheduleEntry?.stepNumber === review.step_number
        && scheduleEntry?.type === 'review'
        && scheduleEntry?.status === 'sent'
        && hasExplicitMatchingTemplate(review, scheduleEntry);
    })
    .sort((left, right) => right.step_number - left.step_number)[0] || null;
}

export function buildCycleReviewRows(review) {
  if (!isCycleReviewPayloadExploitable(review)) return [];
  return REVIEW_FIELDS.map((field) => ({
    field,
    label: FIELD_LABELS[field],
    rawValue: review[field] == null ? null : String(review[field]).trim(),
    value: labelForValue(field, review[field]),
  })).filter((row) => row.value !== null);
}

export function compareCycleReviews(currentReview, previousReview) {
  if (
    !isCycleReviewPayloadExploitable(currentReview)
    || !isCycleReviewPayloadExploitable(previousReview)
  ) return null;
  const currentByField = new Map(buildCycleReviewRows(currentReview).map((row) => [row.field, row]));
  const previousByField = new Map(buildCycleReviewRows(previousReview).map((row) => [row.field, row]));
  const rows = COMPARABLE_FIELDS.map((field) => {
    const current = currentByField.get(field);
    const previous = previousByField.get(field);
    if (!current || !previous) return null;
    const direction = current.rawValue === previous.rawValue ? 'stable' : 'a_valider';
    return {
      field,
      label: FIELD_LABELS[field],
      current: current.value,
      previous: previous.value,
      changed: current.rawValue !== previous.rawValue,
      direction,
      directionLabel: direction === 'stable' ? 'Réponse identique' : 'À valider Anissa',
    };
  }).filter(Boolean);
  return {
    rows,
    comparableCount: rows.length,
    changedCount: rows.filter((row) => row.changed).length,
    stableCount: rows.filter((row) => row.direction === 'stable').length,
    reviewCount: rows.filter((row) => row.direction === 'a_valider').length,
  };
}

export function buildSevenDayFeedbackContext(feedbacks = [], now = Date.now(), available = true) {
  if (available !== true) {
    return { available: false, hasData: false, count: null, axes: [] };
  }
  const nowMs = typeof now === 'number' ? now : timestamp(now);
  if (!Number.isFinite(nowMs)) {
    return { available: false, hasData: false, count: null, axes: [] };
  }
  const upperBound = nowMs;
  const lowerBound = upperBound - (7 * DAY_MS);
  const rows = (Array.isArray(feedbacks) ? feedbacks : []).filter((feedback) => {
    const at = timestamp(feedback?.created_at || feedback?.date);
    return at !== null && at >= lowerBound && at <= upperBound;
  });
  const trends = computeFeedbackTrends(rows);
  return {
    available: true,
    hasData: rows.length > 0,
    count: rows.length,
    axes: trends.axisTrends
      .filter((axis) => axis.total > 0)
      .map((axis) => ({
        axis: axis.axis,
        label: axis.label,
        total: axis.total,
        summary: axis.summary,
      })),
  };
}

export function buildCycleReviewDigest({
  client,
  reviews = [],
  feedbacks = [],
  feedbackStatus = 'ok',
  now = Date.now(),
} = {}) {
  const schedule = buildPackFollowupSchedule(client);
  const scheduled = selectScheduledSubmittedCycleReviews(reviews, client);
  const exploitable = selectCurrentPackCycleReviews(reviews, client);
  const latestScheduled = scheduled.at(-1) || null;
  const latestScheduleEntry = latestScheduled
    ? schedule.find((entry) => entry.stepNumber === latestScheduled.step_number)
    : null;
  const requiresValidation = Boolean(
    latestScheduled
    && (
      !hasExplicitMatchingTemplate(latestScheduled, latestScheduleEntry)
      || !isCycleReviewPayloadExploitable(latestScheduled)
    )
  );
  const current = requiresValidation ? null : latestScheduled;
  const currentTemplate = current?.template_type || null;
  const previous = current
    ? [...exploitable].reverse().find(
      (review) => review?.id !== current.id && review?.template_type === currentTemplate,
    ) || null
    : null;
  const comparison = compareCycleReviews(current, previous);
  const feedbackContext = buildSevenDayFeedbackContext(feedbacks, now, feedbackStatus === 'ok');
  const currentRows = buildCycleReviewRows(current);
  const takeaways = [];

  if (current) {
    currentRows.forEach((row) => takeaways.push(`${row.label} : ${row.value}.`));
    const detail = boundedSingleLine(current.main_issue_text);
    if (detail) takeaways.push(`Précision de la cliente : ${detail}`);
    if (comparison) {
      takeaways.push(`Comparaison factuelle : ${comparison.stableCount} réponse${comparison.stableCount > 1 ? 's' : ''} identique${comparison.stableCount > 1 ? 's' : ''}, ${comparison.reviewCount} changement${comparison.reviewCount > 1 ? 's' : ''} à valider Anissa.`);
    } else {
      takeaways.push('Premier bilan reçu pour cette période : aucune comparaison antérieure.');
    }
    if (!feedbackContext.available) {
      takeaways.push('Ressentis des 7 derniers jours : données indisponibles.');
    } else if (!feedbackContext.hasData) {
      takeaways.push('Ressentis des 7 derniers jours : aucune donnée reçue.');
    } else {
      takeaways.push(`Ressentis reçus sur les 7 derniers jours : ${feedbackContext.count}.`);
    }
  }

  const meetingSummary = current
    ? boundedSingleLine([
      `${reviewWeekLabel(current, schedule)} reçu.`,
      ...takeaways,
    ].join(' '), 1600)
    : '';

  const sent = selectCurrentSentCycleReview(reviews, client);
  return {
    reviews: scheduled.map((review) => ({ ...review, cycle_label: reviewWeekLabel(review, schedule) })),
    sent: sent ? { ...sent, cycle_label: reviewWeekLabel(sent, schedule) } : null,
    current,
    invalidCurrent: requiresValidation ? latestScheduled : null,
    requiresValidation,
    previous,
    currentRows,
    comparison,
    feedbackContext,
    takeaways,
    meetingSummary,
    currentLabel: current ? reviewWeekLabel(current, schedule) : null,
    previousLabel: previous ? reviewWeekLabel(previous, schedule) : null,
  };
}

export function resolveCycleReviewWorkflowIntent(intent, { phaseChangeAvailable = false } = {}) {
  if (intent === null) return { allowed: true, workflowAction: null };
  if (intent === 'continue' || intent === 'adapt') {
    return { allowed: true, workflowAction: intent };
  }
  if (intent === 'changePhase' && phaseChangeAvailable === true) {
    return { allowed: true, workflowAction: 'transition' };
  }
  return { allowed: false, workflowAction: null };
}

export const CYCLE_REVIEW_FIELDS = REVIEW_FIELDS;
